<template>
  <div
    class="input"
    :class="[
      attrs.type,
      {
        hasError,
        hasSuccess,
        isClearable: clearable,
        isTextarea,
        isDisabled: ($attrs.disabled!==undefined && $attrs.disabled!==false),
        hasPicker,
        hasNoLabel: label==='',
        hasClearer: (clearable && (modelValue?.length > 0 || attrs.type=='number' && modelValue))
      }
    ]"
  >
    <div class="input-wrapper">
      <div class="icon-wrapper">
        <button v-if="clearable && (modelValue?.length > 0 || attrs.type=='number' && modelValue)" class="iconButton action-close" tabindex="-1" @click="onClear">
          <Icon name="close-line" color="blue" />
        </button>
        <button v-if="attrs.type === 'password'" class="iconButton action-password" tabindex="-1" @click="showPassword = !showPassword">
          <Icon :name="showPassword ? 'hide-line' : 'show-line'" color="blue" />
        </button>
        <Icon v-if="hasError" name="warning-fill" color="danger" />
        <Icon v-if="hasSuccess" name="success-fill" color="success" />
      </div>
      <component
        :is="isTextarea ? 'textarea' : 'input'"
        :id="id"
        v-bind="$attrs"
        ref="inputRef"
        :value="modelValue"
        :type="inputType"
        @focus="onFocus"
        @blur="onBlur"
        @click="onClick"
        @input="onInput"
      />
      <label :for="id"><span>{{ label }}</span></label>
    </div>
    <div v-if="hasError && errorMessage && !hasSuccess && errorMessage!==''" class="text-danger text-s message-wrapper message-error">
      {{ errorMessage }}
    </div>
    <div v-if="(($slots.hint && persistentHint) || ($slots.hint && !persistentHint && showHint)) && $slots.hint!==''" class="text-s message-wrapper message-hint">
      <slot name="hint"></slot>
    </div>
  </div>
</template>


<script setup>
import { ref, computed, useAttrs, useId } from 'vue'
import Icon from '../Icon'

defineOptions({ inheritAttrs: false })

const emit = defineEmits(['update:modelValue', 'blur', 'focus', 'click'])
const attrs = useAttrs()
const props = defineProps({
  modelValue: {
    type: [String, Number],
  },
  label: {
    type: String,
    default: '',
  },
  hasPicker: {
    type: Boolean,
    default: true,
  },
  hasError: {
    type: Boolean,
    default: false,
  },
  errorMessage: {
    type: String,
    default: null,
  },
  hasSuccess: {
    type: Boolean,
    default: false,
  },
  persistentHint: {
    type: Boolean,
    default: false,
  },
  clearable: {
    type: Boolean,
    default: true,
  },
  isTextarea: {
    type: Boolean,
    default: false,
  },
})

const id = attrs.id ? attrs.id : useId()
const showPassword = ref(false)
const showHint = ref(false)
const inputRef = ref(null)

const inputType = computed(() => {
  if(props.isTextarea){
    return null
  }
  return showPassword.value ? 'text' : attrs.type
})

function onClear() {
  emit('update:modelValue', '')
  inputRef.value.focus()
}

function onInput(event) {
  const { value } = event.target
  emit('update:modelValue', attrs.type === 'number' ? parseInt(value) : value)
}

function onClick(event) {
  if(!props.hasPicker){
    event.preventDefault()
  }
  emit('click', event)
}

function onFocus(e) {
  showHint.value = true
  emit('focus', e)
}

function onBlur(e) {
  showHint.value = false
  emit('blur', e)
}
</script>


<style lang="scss" scoped>
.input {
  display: flex;
  flex-direction: column;
  gap: .25rem;
  width: 100%;

  --borderColor: var(--mint-dark);
  --backgroundColor: var(--white);
  --borderColorDisabled: #a3a3a3;
  --backgroundColorDisabled: #ccc;
  --borderColorFocus: #377;
  --labelHeight: .9rem;
  --borderRadius: .25rem;
  --inputHeight: 3rem;  // another components needs this fixed height for input
}

.input.hasSuccess {
  --borderColor: var(--success);
}

.input.hasError {
  --borderColor: var(--danger);
}

.input.isDisabled {
  --backgroundColor: var(--backgroundColorDisabled);
}

.input .input-wrapper {
  position: relative;
  display: flex;
  flex-direction: column-reverse;
}

.input .input-wrapper:hover {
  --borderColor: var(--mint-darker);
}

.input .input-wrapper > label {
  left: 0;
  top: calc(var(--labelHeight) - .25rem);
  height: calc(var(--labelHeight) + var(--borderRadius));  // needs extra space for letter-basement
  width: 100%;
  font-size: var(--labelHeight);
  line-height: var(--labelHeight);
  display: flex;
  align-items: first baseline;
  gap: .25rem;
  position: relative;
}

.input.hasNoLabel .input-wrapper > label {
  align-items: center;
  gap: 0;
}

.input .input-wrapper > label span {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: inline-block;
  height: 100%;
}

.input .input-wrapper > label::before,
.input .input-wrapper > label::after {
  content: "";
  width: .5rem;
  height: var(--borderRadius);
  border-color: var(--borderColor);
  border-style: solid;
}

.input .input-wrapper > label::before {
  flex-shrink: 0;
  border-radius: var(--borderRadius) 0 0 0;
  border-width: 1px 0 0 1px;
}

.input .input-wrapper > label::after {
  width: auto;
  border-radius: 0 var(--borderRadius) 0 0;
  border-width: 1px 1px 0 0;
  min-width: .5rem;
  flex-grow: 1;
}

.input .input-wrapper > :is(input, textarea) {
  box-sizing: border-box !important;
  outline: 0;
  outline-offset: 0;
  appearance: none !important;
  color: var(--neutral-80);
  font-weight: 700;
  font-size: .9em;
  font-family: inherit;
  background-color: var(--backgroundColor);
  padding: .5rem 2rem .5rem .75rem;
  border: 1px solid var(--borderColor);
  border-radius: var(--borderRadius);
  width: 100%;
  height: var(--inputHeight);
  border-top: none;
}

.input.hasClearer:is(.hasSuccess, .hasError) .input-wrapper > input {
  padding-right: 4.25rem;
}

.input .input-wrapper > textarea {
  height: auto;
  min-height: 4.25rem;
}

.input .input-wrapper > :is(input, textarea)::placeholder {
  color: var(--neutral-40);
}

.input .input-wrapper > input[type='search']::-webkit-search-decoration,
.input .input-wrapper > input[type='search']::-webkit-search-cancel-button,
.input .input-wrapper > input[type='search']::-webkit-search-results-button,
.input .input-wrapper > input[type='search']::-webkit-search-results-decoration {
  display: none;
}

.input .input-wrapper > :is(input, textarea):is(:active, :focus),
.input .input-wrapper > :is(input, textarea):is(:active, :focus) ~ label {
  --borderColor: var(--borderColorFocus);
}

.input.isDisabled .input-wrapper > :is(input, textarea),
.input.isDisabled .input-wrapper > :is(input, textarea) ~ label {
  --borderColor: var(--borderColorDisabled);
  box-shadow: none;
  pointer-events: none;
}

.input.isDisabled .input-wrapper > :is(input, textarea) {
  background-color: var(--backgroundColor);
}

.input.isDisabled .input-wrapper > :is(input, textarea)::placeholder {
  color: var(--neutral-30);
}

.input .input-wrapper > .icon-wrapper {
  display: flex;
  padding-inline: .25rem;
  align-items: center;
  justify-content: flex-end;
  gap: .25rem;
  position: absolute;
  right: 0;
  top: calc(var(--labelHeight) + var(--borderRadius));
  height: var(--inputHeight);
}

.input.isTextarea .input-wrapper > .icon-wrapper {
  flex-direction: column-reverse;
  padding-block: .5rem;
  height: 100%;
}

@supports (not (-moz-appearance: none)) {  /// safari + chrome (webkit)
  .input .input-wrapper > label {
    height: calc(var(--labelHeight) + var(--borderRadius) + -1px);
  }
}

@supports (-moz-appearance: none) {  /// only firefox (moz)
  .input.isTextarea .input-wrapper > label {
    height: calc(var(--labelHeight) + var(--borderRadius) + -1px);
  }
}

.input .input-wrapper > .icon-wrapper > svg {
  margin-inline: .5rem;
}

.input .message-wrapper:empty,
.input:not(.hasPicker) input::-webkit-calendar-picker-indicator {
  display: none;
}

.input .message-hint > :first-child {
  margin-top: 0;
}

.input .message-hint > :last-child{
  margin-bottom: 0;
}

.iconButton {
  display: flex;
  padding: .25rem;
  border: 0;
  background: none;
  cursor: pointer;
  transition: opacity var(--time2);
}

.iconButton:is(:hover, :focus) {
  opacity: .6;
}
</style>
